import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { AsyncSubject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { MfaService } from '../mfa/mfa.service';
import { MFAResult, MFAScope } from '../mfa/mfa-bean';
import { MeSuccessDialogService } from '../me-dialog/me-success/me-success.service';
import { AppAuthenticatorApprovalComponent } from './app-authenticator-approval/app-authenticator-approval.component';
import {
  AppAuthenticatorEnableNotificationChoice,
  AppAuthenticatorEnableNotificationComponent,
  AppAuthenticatorEnableNotificationDialogData,
} from './app-authenticator-enable-notification/app-authenticator-enable-notification.component';
import {
  AppAuthenticatorNeedsActivateChoice,
  AppAuthenticatorNeedsActivateComponent,
  AppAuthenticatorNeedsActivateDialogData,
} from './app-authenticator-needs-activate/app-authenticator-needs-activate.component';
import { MEError } from '../../error/me-error';
import { MEErrorService } from '../../error/me-error.service';
import {
  BasicAsyncResponseSuccessHandler,
  MEAPIResponse,
  MEEntityAPIResponseHandler,
  ResponseCode,
} from '../../services/api/me-api-response';
import { MEAPIService } from '../../services/api/me-api.service';
import { ExternalSettingsService } from '../../services/external-settings/external-settings.service';
import { UserService } from '../../services/user/user.service';
import { BiometricChallenge, BiometricResultStatus, BiometricService } from 'src/app/system/utility/biometric.service';
import { CryptoService, RsaOaepKeyPair } from 'src/app/system/utility/crypto.service';
import { DeviceInfoService } from 'src/app/system/utility/device-info.service';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { SecureStorageService, SecureStorageType } from 'src/app/system/utility/secure-storage.service';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { PushNotificationService } from 'src/app/system/utility/push-notification.service';
import { CustomDialogWidthConfig, ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import {
  ActiveNotification,
  AppAuthenticatorActivateRequest,
  AppAuthenticatorPushApprovalPayload,
  AppAuthenticatorResult,
  AppAuthenticatorStoredCredential,
  GetActiveNotificationsRequest,
} from './app-authenticator-bean';
import { App } from '@capacitor/app';
import { logger } from '../../services/logging/logging.service';

/** Options for {@link AppAuthenticatorService.activate}. */
export interface AppAuthenticatorActivateOptions {
  /** Skip the confirm dialog and proceed to biometrics + key activation. */
  skipPrompt?: boolean;
  /** When true, cancel does not log the user off (mid-session / transactional activation). */
  keepSessionOnCancel?: boolean;
  /** Called after successful activation instead of getNextCondition. */
  onSuccess?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class AppAuthenticatorService {

  private readonly API_ACTIVATE = MEAPIService.BASE_URL + '/customer/appAuthenticationDevice/activate';
  private readonly API_GET_ACTIVE_NOTIFICATIONS = MEAPIService.BASE_URL + '/customer/getActiveNotifications';
  private readonly COLD_START_DEDUPE_MS = 5000;
  private readonly ACTIVE_NOTIFICATION_TYPE_APP_AUTHENTICATOR = 'APP_AUTHENTICATOR';

  public result!: BehaviorSubject<AppAuthenticatorResult | null>;
  public mfaPending = false;

  private approvalDialogRef!: MatDialogRef<AppAuthenticatorApprovalComponent, boolean>;
  private pushListenerInitialized = false;
  private resumeCheckInFlight = false;
  private resumeCheckQueued = false;
  private lastHandledPassphrase: string | null = null;
  private lastHandledAt = 0;
  private activateOptions: AppAuthenticatorActivateOptions = {};
  /** In-memory keypair from login when no APP_AUTHENTICATOR key exists yet; committed on MFA_AUTH_APP_SELF. */
  private pendingLoginKeyPair: RsaOaepKeyPair | null = null;

  constructor(
    private srvAPI: MEAPIService,
    private srvUser: UserService,
    private srvError: MEErrorService,
    private srvMFA: MfaService,
    private srvSuccess: MeSuccessDialogService,
    private srvBioMetric: BiometricService,
    private srvSecureStorage: SecureStorageService,
    private translate: TranslateUtilityService,
    private srvDialog: MatDialog,
    private srvRpDesign: ResponsiveDesignService,
    private srvPush: PushNotificationService,
    private srvES: ExternalSettingsService,
    private ngZone: NgZone,
  ) { }

  /**
   * Subscribe to App Authenticator push-approval notifications (mobile only).
   * Called once during application init before PushNotificationService.init()
   * so cold-start taps (iOS + Android) are not missed.
   */
  public init(): void {
    if (this.pushListenerInitialized || !PlatformService.isMobile()) {
      return;
    }
    this.pushListenerInitialized = true;

    PushNotificationService.getActionPerformNotifications().subscribe(() => {
      // Capacitor callbacks run outside Angular; MatDialog needs the zone on iOS cold start.
      this.ngZone.run(() => {
        void this.checkActiveNotifications();
      });
    });

    App.addListener('resume', () => {
      this.ngZone.run(() => {
        void this.checkActiveNotifications();
      });
    });

    // Icon launch is a cold start: Capacitor resume does not fire until the app has been backgrounded.
    void this.checkActiveNotifications();
  }

  /**
   * If secure storage has no private key, generate an RSA keypair, hold the private key in
   * memory, and return the public key for login `authKey`. Otherwise returns null (omit authKey).
   * Caller must only invoke this on native mobile when the App Authenticator module is enabled.
   */
  public async prepareLoginAuthKey(): Promise<string | null> {
    const stored = await firstValueFrom(this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY));
    if (stored) {
      this.clearPendingLoginKeyPair();
    }
    else {
      try {
        const keyPair = await CryptoService.generateRsaOaepKeyPair();
        this.pendingLoginKeyPair = keyPair;
        return keyPair.publicKey;
      } catch (err) {
        logger.error(err);
        this.clearPendingLoginKeyPair();
      }
    }
    return null;
  }

  /**
   * Persist the pending login private key to secure storage (MFA_AUTH_APP_SELF).
   * No-op success if there is no pending pair (device already had a key).
   */
  public async commitPendingPrivateKey(userId: string): Promise<boolean> {
    if (!this.pendingLoginKeyPair) {
      return true;
    }

    const credential: AppAuthenticatorStoredCredential = {
      userId: userId ?? '',
      privateKey: this.pendingLoginKeyPair.privateKey,
    };

    const ok = await firstValueFrom(this.srvSecureStorage.set(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY, JSON.stringify(credential)));
    if (ok) {
      this.clearPendingLoginKeyPair();
    }
    return ok;
  }

  public clearPendingLoginKeyPair(): void {
    this.pendingLoginKeyPair = null;
  }

  public hasPendingLoginKeyPair(): boolean {
    return !!this.pendingLoginKeyPair;
  }

  /**
   * App Authenticator activation during login:
   * Web: Back cancels; "Not Now? Send Me a Code" starts standard MFA (activation requires the mobile app)
   * Mobile:
   * 1) Confirm / Cancel, with "Not Now? Send Me a Code" under the buttons
   * 2) Confirm → biometrics then key activation (popup + cancel if biometrics unavailable/fail)
   * 3) Cancel / Back → cancel quietly
   * 4) Send Me a Code → standard MFA
   * 5) On MFA_VALIDATION: MFA with APP_ACTIVATE, then success dialog + getNextCondition
   *
   * @param options.skipPrompt Skip the confirm dialog and go straight to biometrics (mobile mid-flow).
   * @param options.keepSessionOnCancel Do not log off when the user cancels (transactional activation).
   * @param options.onSuccess Called after successful activation instead of getNextCondition.
   */
  public activate(userId: string = '', options: AppAuthenticatorActivateOptions = {}): BehaviorSubject<AppAuthenticatorResult | null> {
    this.clearPendingLoginKeyPair();
    this.result = new BehaviorSubject<AppAuthenticatorResult | null>(null);
    this.mfaPending = false;
    this.activateOptions = options;

    if (options.skipPrompt) {
      this.activateAfterBiometrics(userId);
      return this.result;
    }
    else {
      this.continueAfterActivatePrompt(userId);
      return this.result;
    }
  }

  /**
   * Shows the enable-notification dialog (mobile only). Invoked from the dashboard bell
   * and the App Authenticator login self-auth flow.
   * If permission is already denied, shows guidance to enable from app settings (Dismiss only).
   * Otherwise Enable requests the OS permission after this dialog has closed.
   */
  public async promptEnableNotification(): Promise<void> {
    if (!PlatformService.isMobile()) {
      return;
    }

    const status = await PushNotificationService.getReceivePermissionStatus();
    const permissionDenied = status === 'denied';
    const dialogConfig = new MatDialogConfig<AppAuthenticatorEnableNotificationDialogData>();
    dialogConfig.data = { permissionDenied };
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    const choice: AppAuthenticatorEnableNotificationChoice | undefined = await firstValueFrom(
      this.srvDialog.open(AppAuthenticatorEnableNotificationComponent, dialogConfig).afterClosed()
    );
    if (choice !== 'enable' || permissionDenied) {
      return;
    }
    await this.requestNotificationPermissionAfterPrompt();
  }

  private async requestNotificationPermissionAfterPrompt(): Promise<void> {
    // Let the MatDialog overlay finish dismissing before presenting the OS sheet.
    await new Promise((resolve) => setTimeout(resolve, 350));
    try {
      const granted = await PushNotificationService.requestReceivePermission();
      logger.debug(`Notification permission after Enable: granted=${granted}`);
      if (granted) {
        await this.srvPush.registerIfPermitted();
      }
    } catch (err) {
      logger.error(err);
    }
  }

  private async continueAfterActivatePrompt(userId: string): Promise<void> {
    const choice = await this.showActivatePrompt();
    // true = confirm (mobile), false = cancel/back, null = send me a code
    if (choice === false) {
      this.cancel();
      return;
    }
    if (choice === null) {
      this.fallbackToStandardMfa();
      return;
    }
    await this.activateAfterBiometrics(userId);
  }

  private async showActivatePrompt(): Promise<boolean | null> {
    // Browser: Back + "Not Now? Send Me a Code". Mobile: Cancel / Confirm + same footer.
    const dialogConfig = new MatDialogConfig<AppAuthenticatorNeedsActivateDialogData>();
    dialogConfig.data = {};
    dialogConfig.maxWidth = 600;
    dialogConfig.width = '98%';
    dialogConfig.height = 'auto';
    dialogConfig.panelClass = 'custom-dialog-confirm-messages';
    dialogConfig.disableClose = true;

    const choice: AppAuthenticatorNeedsActivateChoice | undefined = await firstValueFrom(
      this.srvDialog.open(AppAuthenticatorNeedsActivateComponent, dialogConfig).afterClosed()
    );
    switch (choice) {
      case 'continue':
        return true;
      case 'sendCode':
        return null;
      case 'back':
      default:
        return false;
    }
  }

  private async activateAfterBiometrics(userId: string): Promise<void> {
    const ok = await this.validateBiometrics();
    if (ok) {
      await this.activateWithKeyPair(userId);
    }
    else {
      await this.showCancelActivationDialog();
    }
  }

  /**
   * Prompt the OS biometric challenge. If biometrics are disabled on this device,
   * enable the local flag in secure storage only — does not call updateBiometric API.
   */
  private async validateBiometrics(): Promise<boolean> {
    const status = await firstValueFrom(this.srvBioMetric.getPlatformBiometricStatus());
    if (!status.isPlatformSupport || !status.biometricType) {
      return false;
    }

    const challenge: BiometricChallenge = {
      biometricType: status.biometricType,
      description: this.srvBioMetric.getBiometricName(status.biometricType),
    };
    const resultStatus = await firstValueFrom(this.srvBioMetric.performBiometric(challenge));
    return resultStatus === BiometricResultStatus.SUCCESS;
  }

  private async showBiometricsRequired(titleKey: string): Promise<void> {
    await firstValueFrom(this.srvError.showDialog(
      this.translate.instant(titleKey),
      this.translate.instant('APP_AUTHENTICATOR.BIOMETRICS_REQUIRED')
    ));
  }

  private async showCancelActivationDialog(): Promise<void> {
    await this.showBiometricsRequired('APP_AUTHENTICATOR.ACTIVATE_TITLE');
    this.cancel();
  }

  private async activateWithKeyPair(userId: string): Promise<void> {
    let keyPair: RsaOaepKeyPair;
    try {
      keyPair = await CryptoService.generateRsaOaepKeyPair();
    } catch (err) {
      logger.error(err);
      await firstValueFrom(this.srvError.showDialog(
        this.translate.instant('APP_AUTHENTICATOR.ACTIVATE_TITLE'),
        this.translate.instant('APP_AUTHENTICATOR.KEY_GENERATION_FAILED')
      ));
      return this.failActivation();
    }

    const credential: AppAuthenticatorStoredCredential = {
      userId: userId,
      privateKey: keyPair.privateKey,
    };
    const stored = await firstValueFrom(this.srvSecureStorage.set(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY, JSON.stringify(credential)));
    if (!stored) {
      return this.failActivation();
    }

    // Trigger the /customer/appAuthenticationDevice/activate endpoint
    const bean: AppAuthenticatorActivateRequest = {publicKey: keyPair.publicKey};
    const ok = await firstValueFrom(this.srvAPI.Post(this.API_ACTIVATE, bean, [new ActivateMfaRequiredHandler(ResponseCode.MFA_VALIDATION, this)]));
    if (!ok && !this.mfaPending) {
      return this.failActivation();
    }
  }

  /**
   * Called when activate API returns MFA_VALIDATION.
   * Uses a dedicated MFA result subject; mid-session flows restore the caller's subject on success.
   */
  public runActivateMfa(): void {
    this.mfaPending = true;
    this.srvMFA.useStandardMFA();
    this.srvMFA.preauth(MFAScope.APP_AUTHENTICATE_ACTIVATE).subscribe((res: MFAResult) => {
      switch (res) {
        case MFAResult.VERIFIED:
          this.onActivationSuccess();
          break;
        case MFAResult.RETRY:
          break;
        default:
          this.srvBioMetric.clearBiometricAppAuth();
          this.complete(AppAuthenticatorResult.FAILED);
          break;
      }
    });
  }

  private fallbackToStandardMfa(): void {
    this.srvMFA.preauth(MFAScope.LOGIN).subscribe((res: MFAResult) => {
      switch (res) {
        case MFAResult.VERIFIED:
          this.srvUser.getNextCondition();
          this.complete(AppAuthenticatorResult.VERIFIED);
          break;
        case MFAResult.RETRY:
          break;
        default:
          this.complete(AppAuthenticatorResult.FAILED);
          break;
      }
    });
  }

  private async onActivationSuccess(): Promise<void> {
    await firstValueFrom(this.srvSuccess.show(
      this.translate.instant('APP_AUTHENTICATOR.SUCCESS_TITLE'),
      this.translate.instant('APP_AUTHENTICATOR.SUCCESS_MESSAGE')
    ));
    if (this.activateOptions.onSuccess) {
      this.activateOptions.onSuccess();
    } else {
      this.srvUser.getNextCondition();
    }
    this.complete(AppAuthenticatorResult.ACTIVATED);
  }

  private failActivation(): void {
    this.srvBioMetric.clearBiometricAppAuth();
    this.complete(AppAuthenticatorResult.FAILED);
  }

  public cancel(): void {
    if (!this.activateOptions.keepSessionOnCancel) {
      this.srvUser.logoff();
    }
    this.complete(AppAuthenticatorResult.CANCELLED);
  }

  public complete(result: AppAuthenticatorResult): void {
    if (this.result) {
      this.result.next(result);
    }
  }

  /**
   * On icon launch, resume, and push tap, fetch active App Authenticator approvals.
   * A trigger that arrives while a fetch is running is performed once more when it finishes.
   */
  private async checkActiveNotifications(): Promise<void> {
    if (this.resumeCheckInFlight) {
      this.resumeCheckQueued = true;
      return;
    }
    this.resumeCheckInFlight = true;
    try {
      do {
        this.resumeCheckQueued = false;
        try {
          await this.loadActiveNotifications();
        } catch (err) {
          logger.error(err);
        }
      } while (this.resumeCheckQueued);
    } finally {
      this.resumeCheckInFlight = false;
      if (this.resumeCheckQueued) {
        this.resumeCheckQueued = false;
        void this.checkActiveNotifications();
      }
    }
  }

  private async loadActiveNotifications(): Promise<void> {
    const settings = await firstValueFrom(this.srvES.externalSettings$);
    if (!settings.globalVariables.isModuleEnabledTfaByAppAuthenticator) {
      return;
    }

    const stored = await firstValueFrom(this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY));
    if (!stored) {
      return;
    }

    let credential: AppAuthenticatorStoredCredential;
    try {
      credential = JSON.parse(stored);
    } catch (err) {
      logger.error(err);
      return;
    }
    // No stored key: do not call getActiveNotifications.
    if (!credential.userId || !credential.privateKey) {
      return;
    }

    const notifications = await firstValueFrom(this.getActiveNotifications({
      deviceId: DeviceInfoService.getDeviceId(),
      userId: credential.userId,
      timestamp: Date.now(),
    }));
    if (!notifications?.length) {
      return;
    }

    const now = Date.now();
    const pending = notifications
      .filter((item) => item.type === this.ACTIVE_NOTIFICATION_TYPE_APP_AUTHENTICATOR)
      .filter((item) => !item.expiresAt || item.expiresAt >= now)
      .sort((a, b) => a.priority - b.priority);

    if (!pending.length) {
      return;
    }

    const status = await firstValueFrom(this.srvBioMetric.getPlatformBiometricStatus());
    if (!status.isPlatformSupport || !status.biometricType) {
      await this.showBiometricsRequired('APP_AUTHENTICATOR.AUTHENTICATION_REQUEST_TITLE');
      return;
    }

    for (const item of pending) {
      const payload = await this.parseApprovalPayload(item.payload);
      if (!payload) {
        continue;
      }
      if (
        payload.tfaPassphrase === this.lastHandledPassphrase
        && Date.now() - this.lastHandledAt < this.COLD_START_DEDUPE_MS
      ) {
        logger.debug('Ignoring duplicate App Authenticator approval');
        continue;
      }
      if (payload.token && !PushNotificationService.claimToken(payload.token)) {
        logger.debug('Skipping active notification; token already handled', payload.token);
        continue;
      }
      this.lastHandledPassphrase = payload.tfaPassphrase;
      this.lastHandledAt = Date.now();
      await this.showApprovalDialog(payload);
    }
  }

  private getActiveNotifications(bean: GetActiveNotificationsRequest): AsyncSubject<ActiveNotification[] | null> {
    const ret = new AsyncSubject<ActiveNotification[] | null>();
    const posted = this.srvAPI.Post<ActiveNotification[] | null>(
      this.API_GET_ACTIVE_NOTIFICATIONS,
      bean,
      [new BasicAsyncResponseSuccessHandler<ActiveNotification[] | null>(ret)]
    );
    posted.subscribe((ok) => {
      if (!ok && !ret.closed) {
        ret.next(null);
        ret.complete();
      }
    });
    return ret;
  }

  private async parseApprovalPayload(data: any): Promise<AppAuthenticatorPushApprovalPayload | null> {
    if (!data) {
      return null;
    }

    // Active-notification payload: { authInfo } or a nested data.authInfo string.
    const root = data.data ?? data;
    const authInfo: any = root?.authInfo ?? data.authInfo ?? null;

    // Encrypted: compact JWE string; plaintext: object with tfaPassphrase
    if (typeof authInfo === 'string') {
      const decrypted = await this.decryptAuthInfo(authInfo);
      return this.mapAuthInfoToPayload(decrypted, root, data);
    }

    return this.mapAuthInfoToPayload(authInfo, root, data);
  }

  /**
   * Decrypts compact JWE authInfo with the stored App Authenticator private key,
   * then parses the plaintext JSON object.
   */
  private async decryptAuthInfo(encrypted: string): Promise<any> {
    const stored = await firstValueFrom(this.srvSecureStorage.get(SecureStorageType.APP_AUTHENTICATOR_PRIVATE_KEY));
    if (!stored) {
      logger.error('App Authenticator private key not found for push approval');
      return null;
    }

    let privateKey = '';
    try {
      const credential: AppAuthenticatorStoredCredential = JSON.parse(stored);
      privateKey = credential.privateKey ?? '';
    } catch (err) {
      logger.error(err);
      return null;
    }

    if (!privateKey) {
      logger.error('App Authenticator private key missing from stored credential');
      return null;
    }

    try {
      const plain = await CryptoService.decryptCompactJwe(encrypted, privateKey);
      return JSON.parse(plain);
    } catch (err) {
      logger.error(err);
      return null;
    }
  }

  private mapAuthInfoToPayload(
    authInfo: any,
    root: any,
    data: any
  ): AppAuthenticatorPushApprovalPayload | null {
    if (!authInfo?.tfaPassphrase) {
      return null;
    }

    let result : AppAuthenticatorPushApprovalPayload = {
      scope: authInfo.scope ?? root?.scope ?? data.scope ?? MFAScope.LOGIN,
      deviceName: authInfo.deviceName ?? '',
      location: authInfo.location ?? '',
      latitude: authInfo.latitude ?? null,
      longitude: authInfo.longitude ?? null,
      timeStamp: authInfo.timeStamp ?? '',
      tfaPassphrase: String(authInfo.tfaPassphrase),
      token: authInfo.token ?? root?.token ?? data.token,
      amount: authInfo.amount ?? null,
      targetName: authInfo.targetName ?? null,
      cardNumber: authInfo.cardNumber ?? null,
      vizAliasType: authInfo.vizAliasType ?? null,
      vizAliasValue: authInfo.vizAliasValue ?? null,
      currentStatus: authInfo.currentStatus ?? null,
      newStatus: authInfo.newStatus ?? null,
      orderType: authInfo.orderType ?? null,
      authTitle: authInfo.authTitle ?? 'Unknown'
    };

    return result;
  }

  private async showApprovalDialog(payload: AppAuthenticatorPushApprovalPayload): Promise<void> {
    if (this.approvalDialogRef?.getState() === MatDialogState.OPEN) {
      this.approvalDialogRef.close();
    }

    const dialogConfig = new MatDialogConfig<AppAuthenticatorPushApprovalPayload>();
    dialogConfig.data = payload;
    dialogConfig.maxWidth = 600;
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = [
      'custom-dialog-mfa-messages',
      new CustomDialogWidthConfig(this.srvRpDesign).getDialogWidthClass({ XS: '98' }),
    ];

    this.approvalDialogRef = this.srvDialog.open(AppAuthenticatorApprovalComponent, dialogConfig);
    const approved = await firstValueFrom(this.approvalDialogRef.afterClosed());
    if (approved === true) {
      await this.onApprove(payload);
    } else if (approved === false) {
      await this.onReject(payload);
    }
  }

  private async onApprove(payload: AppAuthenticatorPushApprovalPayload): Promise<void> {
    if (payload.token) {
      this.srvMFA.token = payload.token;
    }
    this.srvMFA.scope = payload.scope || MFAScope.LOGIN;

    const status = await firstValueFrom(this.srvBioMetric.getPlatformBiometricStatus());
    if (!status.isPlatformSupport || !status.biometricType) {
      logger.error('Biometrics not available for App Authenticator approval');
      return;
    }

    const challenge: BiometricChallenge = {
      biometricType: status.biometricType,
      description: this.srvBioMetric.getBiometricName(status.biometricType),
    };
    const bioStatus = await firstValueFrom(this.srvBioMetric.performBiometric(challenge));
    if (bioStatus !== BiometricResultStatus.SUCCESS) {
      return;
    }

    const success = await firstValueFrom(this.srvMFA.approve(payload.tfaPassphrase));
    if (!success) {
      return;
    }
    await firstValueFrom(this.srvSuccess.show(
      this.translate.instant('APP_AUTHENTICATOR.APPROVAL_SUCCESS_TITLE'),
      this.translate.instant('APP_AUTHENTICATOR.APPROVAL_SUCCESS_MESSAGE')
    ));
  }

  private async onReject(payload: AppAuthenticatorPushApprovalPayload): Promise<void> {
    if (payload.token) {
      this.srvMFA.token = payload.token;
    }
    this.srvMFA.scope = payload.scope || MFAScope.LOGIN;
    const success = await firstValueFrom(this.srvMFA.deny(payload.tfaPassphrase));
    if (!success) {
      return;
    }
    await firstValueFrom(this.srvMFA.showAppAuthDenySuccessDialog());
  }

  /**
   * MFA_CODE_EXPIRED (2046) from push approval (appAuthApprove).
   * Show the expired-notification dialog, then close the app.
   */
  public async handlePushApprovalExpired(): Promise<void> {
    await firstValueFrom(this.srvError.showDialog(
      this.translate.instant('APP_AUTHENTICATOR.NOTIFICATION_EXPIRED_TITLE'),
      this.translate.instant('APP_AUTHENTICATOR.NOTIFICATION_EXPIRED_MESSAGE')
    ));
  }
}

class ActivateMfaRequiredHandler implements MEEntityAPIResponseHandler<any> {
  constructor(public readonly code: ResponseCode, private readonly srvAppAuth: AppAuthenticatorService) { }

  handle(_response: MEAPIResponse<any>): boolean | MEError {
    this.srvAppAuth.runActivateMfa();
    return false;
  }
}
